/**
 * Navigation catalog: dashboards → tabs.
 *
 * Shared by the router (which tabs exist) and the API (which reports exist).
 * The widgets of each tab are defined server-side in `server/reports/`, keyed
 * by `${dashboard}/${tab}`; a test asserts the two stay in step.
 *
 * `heading` / `subtitle` (optional) are the page's title and one-line purpose
 * when they differ from the tab title and the dashboard description.
 *
 * `module` ties a tab to a tenant module (see shared/tenants.js) so tabs a
 * tenant does not have are hidden. `permission` is the page permission a user
 * needs for the tab (shared/permissions.js), checked per tenant.
 *
 * `platform` ties a tab to a tenant platform (`web` or `app`, see
 * `platforms` in shared/tenants.js); tenants not live there do not get it.
 *
 * `fresh` marks tabs of the new Manage view (not one of the existing reports);
 * tenants with `reportsOnly` (shared/tenants.js) do not get them.
 *
 * Modules with no tracking data (Stories, Podcasts, Trends, Loyalty,
 * Android/iOS splits) are not listed: a tab is added once its data exists.
 */

import { P, TYPE_PERMISSION } from "./permissions.js";

export const DASHBOARDS = [
  {
    // The content manager's starting point (mockup screens 01–02): items to act on, not traffic.
    id: "manage",
    title: "Manage",
    description: "What needs attention: launches with no recorded opening and webinars losing registrants.",
    tabs: [
      {
        id: "overview",
        title: "Manager overview",
        heading: "What needs your team's attention?",
        subtitle: "Choose an item, inspect the reason, and assign the next step.",
        permission: P.contentOverview,
        fresh: true,
      },
      {
        id: "launch",
        title: "Launch follow-up",
        subtitle: "Find every new item, including those with no recorded opens.",
        permission: P.contentOverview,
        fresh: true,
      },
    ],
  },
  {
    id: "acquisition",
    title: "Acquisition",
    description: "How users arrive, register and return, by platform and source.",
    tabs: [
      { id: "overview", title: "Overview", permission: P.acquisition },
      { id: "web", title: "Web", platform: "web", permission: P.acquisition },
      { id: "app", title: "App", platform: "app", permission: P.acquisition },
      { id: "funnel", title: "User funnel", permission: P.campaigns },
    ],
  },
  {
    id: "engagement",
    title: "Content engagement",
    description: "How each content format performs, who consumes it, and when.",
    tabs: [
      { id: "overview", title: "Overview", permission: P.contentOverview },
      { id: "video", title: "Videos", module: "video", permission: TYPE_PERMISSION.video },
      { id: "reels", title: "Reels", module: "reels", permission: TYPE_PERMISSION.reels },
      { id: "series", title: "Series", module: "series", permission: TYPE_PERMISSION.series },
      { id: "medwiki", title: "MedWiki", module: "medwiki", permission: TYPE_PERMISSION.medwiki },
      { id: "webinar", title: "Webinars", module: "session", permission: TYPE_PERMISSION.session },
      { id: "lms", title: "Courses (LMS)", module: "training", permission: TYPE_PERMISSION.training },
      { id: "surveys", title: "Surveys", module: "survey", permission: TYPE_PERMISSION.survey },
      { id: "epub", title: "eBooks", module: "epub", permission: TYPE_PERMISSION.epub },
      { id: "channels", title: "Channels", module: "channel", permission: TYPE_PERMISSION.channel },
      { id: "behaviour", title: "User behaviour", permission: P.contentOverview },
      { id: "speciality", title: "Speciality count", permission: P.audience },
    ],
  },
  {
    id: "monitoring",
    title: "Monitoring",
    description: "Unusual activity worth a look: automated use, inflated views, click storms, crowded IPs and traffic anomalies.",
    tabs: [{ id: "unusual", title: "Unusual activity", permission: P.monitoring }],
  },
];

/** The engagement tab that lists each content type (content details pages belong to it). */
export const TAB_FOR_CONTENT_TYPE = {
  video: "video",
  reels: "reels",
  series: "series",
  medwiki: "medwiki",
  session: "webinar",
  training: "lms",
  survey: "surveys",
  epub: "epub",
  channel: "channels",
};

/** Dashboard and tab a route belongs to; a content details page belongs to its type's tab. */
export function routeLocation(params) {
  if (params.uid) return { dashboard: "user", tab: null };
  if (params.type) return { dashboard: "engagement", tab: TAB_FOR_CONTENT_TYPE[params.type] ?? "overview" };
  return { dashboard: params.dashboard, tab: params.tab };
}

export function findDashboard(id) {
  return DASHBOARDS.find((d) => d.id === id) ?? null;
}

export function findTab(dashboardId, tabId) {
  return findDashboard(dashboardId)?.tabs.find((t) => t.id === tabId) ?? null;
}

/** Every `${dashboard}/${tab}` key. */
export function allTabKeys() {
  return DASHBOARDS.flatMap((d) => d.tabs.map((t) => `${d.id}/${t.id}`));
}
